import './MultiPageGivingCheckout.scss';

import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import React, { FunctionComponent, useContext, useEffect } from 'react';

import { AuthContext } from '@justgiving/auth-react';
import ErrorPage from '@justgiving/error-page';
import { FormContext } from '../../components/FormContext/FormContext';
import Loader from '@justgiving/loading';
import { PAGES } from '../../constants/pages';
import config from '../../config';
import { createGraphQlClient } from '../../graphQl/baseApi';
import { useChrome } from '../../hooks/useChrome';
import { useDonationLinkQuery } from '../../../types/__generated-graphQL__';
import { useQueryParameters } from '../../hooks/useQueryParameters';
import { useTranslation } from 'react-i18next';

const MultiPageGivingCheckout: FunctionComponent = () => {
  useChrome();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const query = useQueryParameters();
  const linkId = query.get('id');
  const auth = useContext(AuthContext);

  const { isLoading, isError, data, isSuccess } = useDonationLinkQuery(
    createGraphQlClient(auth),
    {
      input: { donationLinkId: linkId ?? '' },
    },
    {
      enabled: Boolean(linkId),
    },
  );
  useEffect(() => {
    if (!Boolean(linkId)) {
      navigate(`${PAGES.Frequency}${location.search}`);
    }
  }, [linkId]); // eslint-disable-line react-hooks/exhaustive-deps

  const errorPageStrings = {
    fourHundred: {
      message: t('error.400.message'),
      subMessage: t('error.400.subMessage'),
    },
    fiveHundred: {
      message: t('error.500.message'),
      subMessage: t('error.500.subMessage'),
    },
    redirectLabel: t('error.redirectLabel'),
    redirectUrl: config.pathPrefix,
  };

  if (isSuccess && !data?.donationLink) {
    return (
      <ErrorPage
        status={404}
        errorMessage={t('error.400.cause')}
        strings={errorPageStrings}
      />
    );
  }

  if (isError) {
    return <ErrorPage status={500} errorMessage={t('error.500.cause')} />;
  }

  if (isLoading) {
    return (
      <div className="giving-checkout-loading">
        <Loader
          colour="grey"
          size="large"
          accessibleLabel={t('common.loading')}
        />
      </div>
    );
  }

  return (
    <FormContext>
      <div className="giving-checkout-wrapper">
        <Outlet />
      </div>
    </FormContext>
  );
};

export default MultiPageGivingCheckout;
